import React, { useRef, useState } from 'react';
import styled, { css } from 'styled-components/macro';
import { useOnClickOutside } from '../helpers/hooks/useOnClickOutside';
import { ReactComponent as MoreIcon } from '../images/icons/more.svg';
import {
  colourNeutral02,
  colourNeutral03,
  colourNeutral05,
  colourNeutral09,
  colourNeutral10,
} from '../styling/colours';
import { fontFamily, fontSize02, fontSize04 } from '../styling/fonts';
import { shadow2 } from '../styling/shadows';
import { spacing02, spacing03, spacing04 } from '../styling/spacing';

type Props = {
  children?: React.ReactNode;
  testId?: string;
  upwards?: boolean;
};

export const MenuButton = ({ children, testId, upwards }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(menuRef, () => setIsOpen(false));

  return (
    <Container>
      <Button onClick={() => setIsOpen(true)} data-testid={testId}>
        <MoreIcon />
      </Button>
      {isOpen && (
        <Menu ref={menuRef} $upwards={upwards}>
          {children}
        </Menu>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: inline-block;
  position: relative;
`;

const Button = styled.button.attrs({ type: 'button' })`
  width: 40px;
  height: 40px;
  outline: none;
  border: none;
  background-color: transparent;
  transition: all 0.25s ease;
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  align-items: center;

  &:hover,
  &:focus {
    opacity: 0.7;
  }

  svg {
    color: ${colourNeutral03};
    height: 100%;
    width: 100%;
  }
`;

const Menu = styled.div<{ $upwards?: boolean }>`
  box-shadow: ${shadow2};
  position: absolute;
  background-color: white;
  width: 200px;
  ${props =>
    props.$upwards
      ? css`
          bottom: 40px;
        `
      : css`
          top: 40px;
        `};
  right: 10px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  z-index: 10;
`;

export const MenuButtonItem = styled.button`
  font-family: ${fontFamily};
  font-size: ${fontSize02};
  outline: none;
  border: none;
  background-color: transparent;
  text-align: left;
  color: ${colourNeutral02};
  padding: ${spacing03} ${spacing04};
  transition: all 0.25s ease;
  cursor: pointer;
  display: flex;
  justify-content: flex-start;
  align-items: center;

  &:hover,
  &:focus {
    background-color: ${colourNeutral10};
  }

  &:disabled {
    color: ${colourNeutral05};
    cursor: not-allowed;

    &:hover,
    &:focus {
      background-color: transparent;
    }
  }

  svg {
    height: ${fontSize04};
    width: ${fontSize04};
    min-width: ${fontSize04};
    margin-right: ${spacing02};
  }
`;

export const MenuButtonDivider = styled.div`
  height: 1px;
  background-color: ${colourNeutral09};
  width: 100%;
`;
